/**
 * DualOptionsCard - Shows two equal booking options for extended distance (currently unused — 50+ is inquiry only)
 * "We Come to You" (direct booking with surcharge) vs "You Come to Us" (hosted inquiry)
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { EventPricingResponse } from '../../api/events';
import { formatPrice } from '../../api/events';
import { subscribeInterest } from '../../api/inquiries';
import { getFriendlyErrorMessage } from '../../api/client-error';

interface DualOptionsCardProps {
  pricing: EventPricingResponse;
  tourStopId: string;
  showId: string;
  locationQuery: string;
}

export function DualOptionsCard({ pricing, tourStopId, showId, locationQuery }: DualOptionsCardProps) {
  const minPrice = Math.min(...pricing.class_options.map((o) => o.total_price_cents));

  const [notifyEmail, setNotifyEmail] = useState(() => {
    try {
      const profileJson = localStorage.getItem('sda_user_profile');
      if (profileJson) {
        const profile = JSON.parse(profileJson) as { email?: string };
        return profile.email || '';
      }
    } catch { /* ignore */ }
    return '';
  });
  const [notifyName, setNotifyName] = useState('');
  const [notifyStatus, setNotifyStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [notifyError, setNotifyError] = useState('');

  const handleNotifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!notifyEmail.trim() || !notifyName.trim()) return;
    setNotifyStatus('submitting');
    setNotifyError('');
    try {
      await subscribeInterest({ email: notifyEmail.trim(), name: notifyName.trim(), show_id: showId });
      setNotifyStatus('success');
    } catch (err) {
      setNotifyError(getFriendlyErrorMessage(err, 'Failed to subscribe'));
      setNotifyStatus('error');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-700 mb-3">
          Extended Distance
        </span>
        <h3 className="text-xl font-display font-bold text-warm-900">
          Two Ways to Book
        </h3>
        <p className="text-warm-600 mt-2">
          At {Math.round(pricing.distance_miles)} miles away, you have two equally great options:
        </p>
      </div>

      {/* Options Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Option 1: We Come to You */}
        <div className="card border-2 border-warm-200 hover:border-primary-400 transition-colors">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
              <svg
                className="h-6 w-6 text-primary-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-warm-900">
              We Come to You
            </h4>
          </div>

          <p className="text-warm-600 mb-4">
            A cast member travels to your studio for the masterclass.
            Perfect for studios that prefer their familiar space.
          </p>

          <ul className="space-y-2 mb-6 text-sm">
            <li className="flex items-start gap-2">
              <svg
                className="h-5 w-5 text-success-500 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-warm-700">Class at your studio</span>
            </li>
            <li className="flex items-start gap-2">
              <svg
                className="h-5 w-5 text-success-500 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-warm-700">No travel for your students</span>
            </li>
            <li className="flex items-start gap-2">
              <svg
                className="h-5 w-5 text-warning-500 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <span className="text-warm-700">Travel surcharge applies</span>
            </li>
          </ul>

          <div className="border-t border-warm-100 pt-4 mb-4">
            <div className="text-sm text-warm-500">Starting at</div>
            <div className="text-2xl font-bold text-warm-900">
              {formatPrice(minPrice)}
            </div>
          </div>

          <Link
            to={`/booking/${tourStopId}?${locationQuery}&path=direct`}
            className="btn-primary w-full text-center"
          >
            Book Direct
          </Link>
        </div>

        {/* Option 2: You Come to Us */}
        <div className="card border-2 border-warm-200 hover:border-primary-400 transition-colors">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
              <svg
                className="h-6 w-6 text-primary-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-warm-900">
              You Come to Us
            </h4>
          </div>

          <p className="text-warm-600 mb-4">
            Bring your students to a venue near the tour location.
            A unique field trip experience for your dancers.
          </p>

          <ul className="space-y-2 mb-6 text-sm">
            <li className="flex items-start gap-2">
              <svg
                className="h-5 w-5 text-success-500 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-warm-700">No travel surcharge</span>
            </li>
            <li className="flex items-start gap-2">
              <svg
                className="h-5 w-5 text-success-500 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-warm-700">Unique field trip experience</span>
            </li>
            <li className="flex items-start gap-2">
              <svg
                className="h-5 w-5 text-warning-500 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <span className="text-warm-700">Travel required for your group</span>
            </li>
          </ul>

          <div className="border-t border-warm-100 pt-4 mb-4">
            <div className="text-sm text-warm-500">Venue near tour location</div>
            <div className="text-lg font-semibold text-warm-700">
              Arranged upon inquiry
            </div>
          </div>

          <Link
            to={`/inquiry/${tourStopId}?${locationQuery}&type=hosted`}
            className="btn w-full text-center border-2 border-primary-500 text-primary-700 hover:bg-primary-50"
          >
            Submit Inquiry
          </Link>
        </div>
      </div>

      {/* Help Text */}
      <p className="text-center text-sm text-warm-500">
        Not sure which option is right for you?{' '}
        <a href="mailto:classes@stagedooraccess.com" className="text-primary-600 hover:underline">
          Contact us
        </a>{' '}
        and we'll help you decide.
      </p>

      {/* Notify Me Section */}
      <div className="bg-warm-50 rounded-xl p-5 border border-warm-200">
        <p className="text-sm text-warm-700 font-medium mb-3">
          Want to be notified when this show comes to your area?
        </p>
        {notifyStatus === 'success' ? (
          <p className="text-sm text-success-700 bg-success-50 rounded-lg px-4 py-3">
            You'll be notified when this show tours near you!
          </p>
        ) : (
          <form onSubmit={handleNotifySubmit} className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={notifyName}
              onChange={(e) => setNotifyName(e.target.value)}
              placeholder="Your name"
              required
              className="flex-1 px-3 py-2 border border-warm-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <input
              type="email"
              value={notifyEmail}
              onChange={(e) => setNotifyEmail(e.target.value)}
              placeholder="Your email"
              required
              className="flex-1 px-3 py-2 border border-warm-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <button
              type="submit"
              disabled={notifyStatus === 'submitting'}
              className="px-4 py-2 border-2 border-primary-500 text-primary-700 rounded-lg text-sm font-medium hover:bg-primary-50 disabled:opacity-50 transition-colors whitespace-nowrap"
            >
              {notifyStatus === 'submitting' ? 'Subscribing...' : 'Notify Me'}
            </button>
          </form>
        )}
        {notifyStatus === 'error' && notifyError && (
          <p className="text-sm text-danger-600 mt-2">{notifyError}</p>
        )}
      </div>
    </div>
  );
}

export default DualOptionsCard;
